import { DOCUMENT, NgFor, NgIf } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  inject
} from "@angular/core";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";

import { LocalStorageService } from "src/app/services/local-storage.service";
import { LocalStorageKeys } from "src/constants/local-storage-keys";
import { TEAM_ROUTES } from "src/constants/team";
import { BillingPlanEnum, UserRoleEnum } from "../../../interface";
import { ModalService } from "../../services/modal.service";
import { UserService } from "../../services/user.service";
import { RootStore } from "../../store/root.store";
import { InitialsPipe } from "../shared/pipes/initials.pipe";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  imports: [RouterLink, NgIf, RouterLinkActive, NgFor, InitialsPipe],
  standalone: true
})
export class HeaderComponent implements OnInit {
  rootStore = inject(RootStore);
  router = inject(Router);

  private document = inject<Document>(DOCUMENT);
  private cd = inject(ChangeDetectorRef);
  private userService = inject(UserService);
  private modalService = inject(ModalService);
  private localStorageService = inject(LocalStorageService);

  @ViewChild("dropdownMenu") dropdownMenu!: ElementRef;
  @ViewChild("orgDropdownMenu") orgDropdownMenu!: ElementRef;

  dropdown = false;
  orgDropdown = false;
  teamActive = false;
  playerActive = false;

  switchingOrganization = false;

  trialExpirationWarning: boolean;
  trialExpirationDays: number;
  trialExpirationHours: number;
  trialExpiredWarning: boolean;
  userRoleEnum = UserRoleEnum;
  isMobileMenuOpen = false;

  ngOnInit() {
    this.setTrialEndingWarning();
    this.rootStore.userStore.orgChange$.subscribe(() => {
      this.setTrialEndingWarning();
    });
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdown || this.orgDropdown) {
      const clickedInside = this.dropdownMenu?.nativeElement.contains(event.target);
      const clickedInsideOrg = this.orgDropdownMenu?.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.hideDropdown();
      }
      if (!clickedInsideOrg) {
        this.hideOrgDropdown();
      }
    }
  }

  hideDropdown() {
    this.dropdown = false;
    this.cd.markForCheck();
  }

  toggleDropdown() {
    this.dropdown = !this.dropdown;
  }

  hideOrgDropdown() {
    this.orgDropdown = false;
    this.cd.markForCheck();
  }

  toggleOrgDropdown() {
    if (this.rootStore.userStore.userData?.role === this.userRoleEnum.Admin) {
      this.orgDropdown = !this.orgDropdown;
    }
  }

  userSettings() {
    this.hideDropdown();
    this.router.navigateByUrl("/user-settings");
  }

  orgSettings() {
    this.hideOrgDropdown();
    this.router.navigateByUrl("/options/organization");
  }

  switchOrganization(id: string) {
    if (id === this.rootStore.userStore.userData.currentOrganization._id) {
      return;
    }

    this.switchingOrganization = true;
    this.userService.switchOrganization(id).subscribe(() => {
      this.switchingOrganization = false;
    });
    this.rootStore.timelineStore.activeProject = null;
    this.rootStore.timelineStore.activeUser = null;
    this.rootStore.timelineStore.freeView = false;
    this.rootStore.timelineStore.busyView = false;
    this.rootStore.timelineStore.users = [];
    this.rootStore.timelineStore.teamTabs = [];
    this.hideOrgDropdown();
    this.hideMobileMenu();

    // TODO -> clear local storage of current org. data
    // TODO -> update store for multiple orgs
  }

  createOrganization() {
    this.hideOrgDropdown();
    this.modalService.showAddOrganizationModal();
  }

  logout() {
    this.rootStore.userStore.clearAuthToken();
    this.router.navigateByUrl("/login");
  }

  getActiveProject() {
    if (this.rootStore.timelineStore.activeProject) {
      return this.rootStore.timelineStore.activeProject._id;
    } else if (this.rootStore.timelineStore.busyView) {
      return TEAM_ROUTES.BUSY;
    } else {
      return (
        this.localStorageService.getItem(
          LocalStorageKeys.LAST_SELECTED_TEAM_OVERVIEW_BY_USER_AND_ORG_ID
        ) ?? TEAM_ROUTES.BUSY
      );
    }
  }

  getActivePlayer() {
    // load active user, else load current user
    if (this.rootStore.timelineStore.activeUser) {
      return this.rootStore.timelineStore.activeUser._id;
    }
    return this.rootStore.userStore.userData?._id;
  }

  hideExpirationWarning() {
    this.trialExpirationWarning = false;
    this.localStorageService.setItem(
      LocalStorageKeys.NO_TRIAL_EXPIRATION_WARNING_BY_ORG_ID,
      "true"
    );
  }

  private setTrialEndingWarning() {
    this.trialExpiredWarning = false;
    this.trialExpirationWarning = false;
    const orgTrialExpiryDate = new Date(
      this.rootStore.userStore.userData?.currentOrganization.trialExpiryDate
    );
    const dayDiff = (+orgTrialExpiryDate - +new Date()) / (1000 * 3600 * 24);
    // show expiry notification to admins
    if (
      this.rootStore.userStore.isAdmin() &&
      this.rootStore.userStore.userData.currentOrganization.billingPlan ===
        BillingPlanEnum.NoPlan &&
      !this.localStorageService.getItem(LocalStorageKeys.NO_TRIAL_EXPIRATION_WARNING_BY_ORG_ID)
    ) {
      if (dayDiff < 14.5 && dayDiff > 0) {
        this.trialExpirationWarning = true;
        this.trialExpirationDays = Math.round(dayDiff);
        if (1 > dayDiff && dayDiff > 0) {
          this.trialExpirationHours = Math.floor(dayDiff * 24);
        }
      }
    }

    // show expired warning to everybody
    if (
      dayDiff < 0 &&
      this.rootStore.userStore.userData.currentOrganization.billingPlan === BillingPlanEnum.NoPlan
    ) {
      this.trialExpiredWarning = true;
    }
  }

  get loginableOrganizations() {
    return this.rootStore.userStore.userData.organizations.filter((org) => org.loginable);
  }

  get currentOrganization() {
    return this.rootStore.userStore.userData.currentOrganization;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  hideMobileMenu() {
    this.isMobileMenuOpen = false;
  }
}
